import React, { useEffect, useMemo, useState } from "react";
import { color } from "src/styles/globals";
import { Title as SundayTitle, Headline } from "sunday-component";
import { Col, Row } from "antd";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { User } from "src/types/user";
import Toast from "src/components/Common/Toast/Toast";
import Divider from "src/components/Common/Divider";
import { logError } from "src/utils/logger";
import { getAffiliateReport } from "src/api/affiliate";
import { formatNumberToThaiCurrency } from "src/utils/format";
import { SectionContainer } from "../style/AffiliatePage.style";
import {
  Body,
  SubTitle,
  ShareLinkContainer,
  FlexVerticalContainer,
  ReportLabel,
  ReportContainer,
  Describe,
  ReportCard,
  ContactButton,
  FillBox,
  DescriptionDialog,
  Button,
  FlexHorizontalContainer,
  CodeInfoContainer,
} from "./AffiliateReport.style";
import ContactModal from "../../Modal/ContactModal";
import { ShareLinkTextBox } from "../ShareLinkTextBox/ShareLinkTextBox";
import PayoutSection from "../PayoutSection/PayoutSection";
import ShareLinkModal from "../ShareLinkModal/ShareLinkModal";

type LabelTooltipDialogProps = {
  label: string;
  title: string;
  description: string;
};
const LabelTooltipDialog = ({
  label,
  title,
  description,
}: LabelTooltipDialogProps) => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation("common");
  return (
    <>
      <Describe onClick={() => setVisible(true)}>
        <ReportLabel>{label}</ReportLabel>
        <Image width={16} height={16} src="/static/help-circle.svg" />
      </Describe>
      <DescriptionDialog
        width={303}
        isVisible={visible}
        title={title}
        body={<Body color={color.charcoal09}>{description}</Body>}
        confirmText={<SubTitle>{t("close")}</SubTitle>}
        onConfirm={() => setVisible(false)}
      />
    </>
  );
};

type ReportBoxProps = {
  label: string;
  dialogTitle: string;
  value: string;
  description: string;
  // eslint-disable-next-line react/require-default-props
  additional?: React.ReactNode;
  // eslint-disable-next-line react/require-default-props
  unit?: string;
};
const ReportBox = ({
  label,
  dialogTitle,
  value,
  description,
  additional,
  unit,
}: ReportBoxProps) => {
  return (
    <FillBox>
      <LabelTooltipDialog
        title={dialogTitle}
        label={label}
        description={description}
      />
      <SundayTitle level={7}>{value}</SundayTitle>
      {additional}
      {unit && <Body color={color.charcoal08}>{unit}</Body>}
    </FillBox>
  );
};

type AffiliateReportProps = {
  channelConfig: any;
  user: User | null;
};
const AffiliateReport = ({ channelConfig, user }: AffiliateReportProps) => {
  const sRefCode = user?.affiliate?.sRefCode;
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [isShareLinkModalVisible, setIsShareLinkModalVisible] = useState(false);
  const [report, setReport] = useState<any>({
    earningAmount: 0,
    bonusAmount: 0,
    reviewAmount: 0,
    bonusReviewAmount: 0,
    purchasedCount: 0,
    cumulativeAmount: 0,
    unsuccessfulCount: 0,
  });
  const { t } = useTranslation("affiliate");
  const shareLinkUrl = `${process.env.NEXT_PUBLIC_BASE_PATH}/?sRefCode=${sRefCode}`;

  const onWriteClipboard = () => {
    navigator.clipboard.writeText(shareLinkUrl);
    Toast(t("common:toast.copy"));
  };

  const bankAccountInfo = useMemo(() => {
    try {
      return user?.profile?.identities?.bankaccount
        ? JSON.parse(user?.profile?.identities?.bankaccount)
        : undefined;
    } catch (error) {
      logError(
        `[affiliate-report] fail to parse bank account`,
        { suid: user.suid },
        error
      );

      return {};
    }
  }, [user]);

  const addressInfo = useMemo(() => {
    try {
      return user?.profile?.contacts?.homeaddress
        ? JSON.parse(user?.profile?.contacts?.homeaddress)
        : undefined;
    } catch (error) {
      logError(
        `[affiliate-report] fail to parse address info`,
        { suid: user.suid },
        error
      );

      return {};
    }
  }, [user]);

  useEffect(() => {
    const fetchAffiliateReport = async () => {
      if (sRefCode) {
        const result = await getAffiliateReport();
        if (result) {
          setReport(result);
        }
      }
    };
    fetchAffiliateReport();
  }, [sRefCode]);

  if (!sRefCode) {
    return <></>;
  }

  return (
    <SectionContainer>
      <ReportContainer>
        <CodeInfoContainer>
          <div>
            <SubTitle>{t("report.yourCode")}</SubTitle>
            <Body>{sRefCode}</Body>
          </div>
          <ShareLinkContainer columnGap="8px">
            <ShareLinkTextBox
              onWriteClipboard={onWriteClipboard}
              shareLinkUrl={shareLinkUrl}
            />
            <Button onClick={() => setIsShareLinkModalVisible(true)}>
              <SubTitle>{t("report.shareLink")}</SubTitle>
            </Button>
          </ShareLinkContainer>
        </CodeInfoContainer>
        <ReportCard
          type="inner"
          title={<Headline level={6}>{t("report.cardTitle")}</Headline>}
          id="report"
        >
          <Row>
            <Col span={12}>
              <LabelTooltipDialog
                label={t("report.earnAmount")}
                title={t("report.earnAmountTooltip.title")}
                description={t("report.earnAmountTooltip.description")}
              />
              <SundayTitle level={1}>
                {formatNumberToThaiCurrency(report.earningAmount)}
              </SundayTitle>
            </Col>
            <Col
              span={12}
              style={{
                borderLeft: `1px solid ${color.charcoal03}`,
                paddingLeft: "16px",
              }}
            >
              <LabelTooltipDialog
                label={t("report.bonusAmount")}
                title={t("report.bonusAmountTooltip.title")}
                description={t("report.bonusAmountTooltip.description")}
              />
              <SundayTitle level={1}>
                {formatNumberToThaiCurrency(report.bonusAmount)}
              </SundayTitle>
            </Col>
          </Row>
          <Divider />
          <PayoutSection
            bankAccountInfo={bankAccountInfo}
            addressInfo={addressInfo}
            personalInfo={{
              identityNumber: user?.profile?.identities?.citizenid,
            }}
          />
          <FlexVerticalContainer rowGap="24px" style={{ marginTop: "24px" }}>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <ReportBox
                  label={t("report.pendingAmount")}
                  dialogTitle={t("report.pendingAmountTooltip.title")}
                  value={formatNumberToThaiCurrency(report.reviewAmount)}
                  description={t("report.pendingAmountTooltip.description")}
                  additional={
                    !!report.bonusReviewAmount && (
                      <FlexHorizontalContainer columnGap="4px">
                        <SundayTitle level={7}>
                          {formatNumberToThaiCurrency(report.bonusReviewAmount)}
                        </SundayTitle>
                        <Body color={color.charcoal08}>
                          ({t("report.bonus")})
                        </Body>
                      </FlexHorizontalContainer>
                    )
                  }
                />
              </Col>
              <Col span={12}>
                <ReportBox
                  label={t("report.successReferee")}
                  dialogTitle={t("report.successRefereeTooltip.title")}
                  value={report.purchasedCount.toLocaleString()}
                  description={t("report.successRefereeTooltip.description")}
                  unit={t("report.policyUnit")}
                />
              </Col>
              <Col span={12}>
                <ReportBox
                  label={t("report.cumulative")}
                  dialogTitle={t("report.summaryEarnTooltip.title")}
                  value={formatNumberToThaiCurrency(report.cumulativeAmount)}
                  description={t("report.summaryEarnTooltip.description")}
                />
              </Col>
              <Col span={12}>
                <ReportBox
                  label={t("report.unsuccessful")}
                  dialogTitle={t("report.unsuccessfulTooltip.title")}
                  value={report.unsuccessfulCount.toLocaleString()}
                  description={t("report.unsuccessfulTooltip.description")}
                  unit={t("report.policyUnit")}
                />
              </Col>
            </Row>
            <Body>
              {t("report.note")}{" "}
              <ContactButton
                type="text"
                onClick={() => setIsContactModalVisible(true)}
              >
                <Body style={{ fontWeight: "bold" }}>
                  {t("report.contact")}
                </Body>
              </ContactButton>
            </Body>
          </FlexVerticalContainer>
        </ReportCard>
      </ReportContainer>
      <ContactModal
        visible={isContactModalVisible}
        contact={channelConfig.content.contacts}
        onCloseModal={() => setIsContactModalVisible(false)}
      />
      <ShareLinkModal
        visible={isShareLinkModalVisible}
        shareLinkUrl={shareLinkUrl}
        onWriteClipboard={onWriteClipboard}
        onClose={() => setIsShareLinkModalVisible(false)}
      />
    </SectionContainer>
  );
};

export default AffiliateReport;
